import {Injectable} from '@angular/core';
import {environment} from '../environments/environment';
import {HttpClient, HttpContext} from '@angular/common/http';
import {Auth} from '../models/auth';
import {BehaviorSubject, firstValueFrom, map} from 'rxjs';
import {StorageService} from './storage-service';
import {SKIP_ERROR_INTERCEPTOR} from '../app/interceptors/error.interceptor';
import {User} from '../models/user';

const API = environment.API;

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  currentUser$ = new BehaviorSubject<any>(null);

  constructor(private http: HttpClient,
              private storageService: StorageService) {
  }

  loadCurrentUser() {
    let token = this.storageService.get(StorageService.TOKEN);

    // Not logged in
    if (!token) {
      return Promise.resolve()
    }

    /**
     * HACK START: 2025-06-28
     *
     * There are some authorization tokens that need to be migrated from the old website to the new website.
     * The old website uses a JSON token while the new website uses a string token. So ... we need to convert
     * the old token to the new token.
     */
    try {
      let tokenJSON = JSON.parse(token); // Attempt to parse the string as JSON
      if (tokenJSON['data']) {
        this.storageService.clear()

        const latestVersion = environment.CLIENT_VERSION
        this.storageService.add(StorageService.CLIENT_VERSION, latestVersion)
        this.storageService.add(StorageService.TOKEN, tokenJSON['data'])
      }
    } catch (e) {
      // Do nothing
    }
    /**
     * HACK END
     */

    return firstValueFrom(this.getUserInfo()).then(currentUser => {

      // Store in session
      this.storageService.add(StorageService.CURRENT_USER, JSON.stringify(currentUser))

      // Broadcast
      this.currentUser$.next(currentUser);

      return Promise.resolve()
    }).catch(error => {

      // Clear sessions
      this.logout()

      return Promise.resolve()
    });
  }

  refreshCurrentUser() {
    this.getUserInfo().subscribe({
      next: (currentUser: User) => {

        // Store in session
        this.storageService.add(StorageService.CURRENT_USER, JSON.stringify(currentUser))

        // Broadcast
        this.currentUser$.next(currentUser);
      },
      error: (error) => {

        // Clear sessions
        this.logout()
      }
    });
  }

  // TODO - This endpoint should be moved to an auth resource name instead of a user.
  login(email: string, password: string) {
    return this.http.post<Auth>(`${API}/api/users/login`, {email, password}).pipe(
      map((auth) => {
        // Store in session
        this.storageService.add(StorageService.TOKEN, auth.token)
        this.storageService.add(StorageService.CURRENT_USER, JSON.stringify(auth.currentUser))

        // Broadcast
        this.currentUser$.next(auth.currentUser);
      }))
  }

  logout() {

    // Store in session
    this.storageService.remove(StorageService.TOKEN)
    this.storageService.remove(StorageService.CURRENT_USER)

    // Broadcast
    this.currentUser$.next(null);
  }

  getUserInfo() {
    return this.http.get<User>(`${API}/api/users/userInfo`);
  }

  resetPassword(email: string) {
    return this.http.post(`${API}/api/users/password/forgot`, {email})
  }

  validatePasswordToken(token: string) {
    return this.http.post(`${API}/api/users/password/validatetoken`, {token}, {
      context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true)
    });
  }

  updatePasswordWithOldPassword(oldPassword: string, password: string) {
    return this.http.put(`${API}/api/users/password`, {oldPassword, password});
  }

  updatePasswordWithToken(token: string, password: string) {
    return this.http.put(`${API}/api/users/password/token`, {token, password});
  }


}
